import React, { useEffect, useState } from "react";
import TextInput from "@peracto/peracto-editor-ui/dist/TextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons/faPlusCircle";
import { faCheckCircle } from "@fortawesome/pro-regular-svg-icons/faCheckCircle";
import BulkSkuUploader from "../../../components/BulkSkuUploader";
import { useConfig } from "@peracto/peracto-config";
import axios from "axios";
import { toast } from "react-toastify";
import TooltipContainer from "../../../components/TooltipContainer";

import styled from "@emotion/styled";

const SKURow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: between;

  .form-group {
    margin: 0;
    width: 100%;
  }

  button {
    width: 24px;
  }
`;

const ProductsForm = (props) => {
  const {
    onChange,
    state: { skus = [""] },
    id,
  } = props;

  const [internalSkus, setInternalSkus] = useState(skus);
  const config = useConfig();
  const API_URL = config.get("api");

  useEffect(() => {
    setInternalSkus(skus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBulkSkusAdded = (newSkus) => {
    setInternalSkus(newSkus);
  };

  const validateSkus = async (skusToValidate) => {
    const filteredSkus = skusToValidate.filter((sku) => !!sku);

    if (filteredSkus.length === 0) {
      return { validSkus: [], invalidSkus: [] };
    }

    try {
      const skuQueryString = filteredSkus
        .map((sku) => `skus[]=${sku}`)
        .join("&");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      const { data } = await axios.get(
        `${API_URL}/storefront/products-by-skus?${skuQueryString}`,
        {
          headers,
        }
      );

      const apiProducts = data?.["hydra:member"] || [];
      const validSkus = [];
      const invalidSkus = [];

      filteredSkus.forEach((sku) => {
        const apiProduct = apiProducts.find((product) => product.sku === sku);
        if (apiProduct) {
          validSkus.push(sku);
        } else {
          invalidSkus.push(sku);
        }
      });

      return { validSkus, invalidSkus };
    } catch (error) {
      console.error("Error validating SKUs:", error);
      // If there's an error, assume all SKUs are invalid
      return { validSkus: [], invalidSkus: filteredSkus };
    }
  };

  return (
    <>
      {internalSkus.map((sku, idx) => {
        return (
          <SKURow className={idx > 0 ? "mt-2" : ""}>
            <TextInput
              label=""
              placeholder="Product SKU"
              onChange={(e) => {
                const skusClone = [...internalSkus];

                skusClone[idx] = e.target.value;
                setInternalSkus(skusClone);
              }}
              value={sku}
            />

            <button
              className="p-0 ml-2 btn btn-danger"
              onClick={(e) => {
                const skusClone = [...internalSkus];

                skusClone.splice(idx, 1);

                setInternalSkus(skusClone);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </SKURow>
        );
      })}

      <hr className="my-2" />

      <div className="flex flex-col gap-y-2">
        <button
          className="btn btn-sm btn-secondary w-100"
          onClick={(e) => {
            e.preventDefault();

            setInternalSkus([...internalSkus, ""]);
          }}
        >
          <FontAwesomeIcon icon={faPlusCircle} className="mr-2" /> Add SKU
        </button>
        <BulkSkuUploader
          onSkusAdded={handleBulkSkusAdded}
          existingSkus={internalSkus}
        />
        <TooltipContainer />
      </div>

      <hr className="my-2" />

      <button
        className="btn btn-sm btn-success w-100"
        onClick={async (e) => {
          e.preventDefault();

          const { validSkus, invalidSkus } = await validateSkus(internalSkus);

          // Update internal SKUs to only include valid ones
          setInternalSkus(validSkus);

          // Show warning toast if there are invalid SKUs
          if (invalidSkus.length > 0) {
            toast.warning(
              <div>
                The following products could not be found:
                <br />
                {invalidSkus.map((sku, index) => (
                  <div key={index}>• {sku}</div>
                ))}
              </div>
            );
          }

          // Apply the valid SKUs
          onChange({
            skus: validSkus,
          });
        }}
      >
        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> Apply SKUs
      </button>
    </>
  );
};

export default ProductsForm;
